import React, { useState, useEffect, useRef } from 'react'
import { formatFileSize, MB } from '../../utils/file'

import styles from './GalleryImage.module.css'

const MAX_FILE_SIZE = 10 * MB
const MIN_DIMENSION = 100   // px
const MAX_DIMENSION = 8000  // px
const ALLOWED_TYPES = ['image/jpeg', 'image/png']

function GalleryImage({ imagePath, className = '', disabled, onRemove, onSelect }) {
  const [showFileInput, setShowFileInput] = useState(!imagePath)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [fileName, setFileName] = useState('')
  const [fileInfo, setFileInfo] = useState(null)
  const [fileError, setFileError] = useState(null)
  const objectUrlRef = useRef(null)

  // Keep showFileInput in sync when imagePath changes
  useEffect(() => {
    setShowFileInput(!imagePath)
    if (imagePath) {
      // A saved image was loaded — reset any pending file state
      setPreviewUrl(null)
      setFileName('')
      setFileInfo(null)
      setFileError(null)
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [imagePath])

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
    }
  }, [])

  const clearPreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    setPreviewUrl(null)
  }

  const handleFileSelect = event => {
    const fileInput = event.target
    const selectedFile = fileInput.files[0]
    if (!selectedFile) return

    setFileError(null)
    clearPreview()

    // Type validation
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setFileError('Only JPEG and PNG images are accepted.')
      setFileName('')
      setFileInfo(null)
      if (onSelect) onSelect(null)
      return
    }

    // Size validation
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError(
        `File is too large (${formatFileSize(selectedFile.size)}). Maximum allowed size is 10 MB.`
      )
      setFileName('')
      setFileInfo(null)
      if (onSelect) onSelect(null)
      return
    }

    // Create preview object URL and check dimensions
    const objUrl = URL.createObjectURL(selectedFile)
    objectUrlRef.current = objUrl

    const img = new window.Image()
    img.onload = () => {
      const w = img.naturalWidth
      const h = img.naturalHeight
      if (w < MIN_DIMENSION || h < MIN_DIMENSION) {
        setFileError(
          `Image is too small (${w}×${h} px). Minimum size is ${MIN_DIMENSION}×${MIN_DIMENSION} px.`
        )
        clearPreview()
        setFileName('')
        setFileInfo(null)
        if (onSelect) onSelect(null)
        return
      }
      if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
        setFileError(
          `Image is too large (${w}×${h} px). Maximum size is ${MAX_DIMENSION}×${MAX_DIMENSION} px.`
        )
        clearPreview()
        setFileName('')
        setFileInfo(null)
        if (onSelect) onSelect(null)
        return
      }
      // Valid — set preview and notify parent
      setPreviewUrl(objUrl)
      setFileName(selectedFile.name)
      setFileInfo({ size: selectedFile.size, width: w, height: h })
      if (onSelect) onSelect(selectedFile)
    }
    img.onerror = () => {
      setFileError('Could not read the image. Please choose a valid JPEG or PNG file.')
      clearPreview()
      setFileName('')
      setFileInfo(null)
      if (onSelect) onSelect(null)
    }
    img.src = objUrl
  }

  return (
    <div className={`galleryImage ${className}`}>
      {/* Saved image display */}
      {imagePath && !previewUrl && (
        <div>
          <div className={styles.imageContainer}>
            <img src={imagePath} alt="Item" />
          </div>
          <div className={styles.galleryControls}>
            <button
              className="btn btn-danger"
              onClick={onRemove}
              disabled={disabled}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* File input */}
      {showFileInput && (
        <div className={styles.fileInput}>
          <label className="input input-file">
            <div className="button">
              <input
                type="file"
                accept="image/jpeg, image/png, image/webp"
                onChange={handleFileSelect}
                disabled={disabled}
              />
              BROWSE
            </div>
            <input
              type="text"
              placeholder="Upload an image (JPEG or PNG, max 10 MB)"
              readOnly
              value={fileName}
              onChange={() => {}}
              disabled={disabled}
            />
          </label>

          {/* Validation error */}
          {fileError && (
            <div className={styles.fileError}>
              <i className="fa fa-exclamation-circle" /> {fileError}
            </div>
          )}

          {/* Preview shown below the file picker */}
          {previewUrl && (
            <div className={styles.previewContainer}>
              <img
                src={previewUrl}
                alt="Preview"
                className={styles.previewImage}
              />
              {fileInfo && (
                <div className={styles.previewMeta}>
                  {fileName} &mdash; {formatFileSize(fileInfo.size)} &mdash;{' '}
                  {fileInfo.width}&times;{fileInfo.height} px
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GalleryImage
